import React, { useState, useRef } from "react";
import axios from "axios";
import { Edit3, Save, Upload as UploadIcon, ExternalLink, X, User } from "lucide-react";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * HG Sea Vessel bio panel — editable bio text, captain image, role title,
 * and 3 candle shop link buttons.
 */
export default function BioPanel({ section, isNight, onUpdated, asPopup = false, onClose }) {
    const accent = isNight ? "#D4AF37" : "#B89B5E";
    const { isAdmin } = useAdmin();
    const bio = section?.bio || { text: "", image_url: "", role_title: "Captain of this Vessel" };
    const links = section?.links?.length ? section.links.slice(0, 3) : [
        { title: "Candle Shop · Etsy", url: "" },
        { title: "Candle Shop · Shopify", url: "" },
        { title: "Candle Shop · Instagram", url: "" },
    ];

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({
        text: bio.text,
        role_title: bio.role_title,
        link1_title: links[0]?.title || "",
        link1_url: links[0]?.url || "",
        link2_title: links[1]?.title || "",
        link2_url: links[1]?.url || "",
        link3_title: links[2]?.title || "",
        link3_url: links[2]?.url || "",
    });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await axios.patch(`${API}/sections/${section.id}`, {
                bio: { text: draft.text, role_title: draft.role_title, image_url: bio.image_url },
                links: [
                    { title: draft.link1_title, url: draft.link1_url },
                    { title: draft.link2_title, url: draft.link2_url },
                    { title: draft.link3_title, url: draft.link3_url },
                ],
            });
            onUpdated && onUpdated(res.data);
            setEditing(false);
        } catch (e) { console.error(e); } finally { setSaving(false); }
    };

    const handleImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await axios.post(`${API}/sections/${section.id}/bio-image`, fd);
            onUpdated && onUpdated(res.data);
        } catch (err) { console.error(err); } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div
            data-testid="bio-panel"
            className={asPopup
                ? "fixed inset-0 z-40 flex items-center justify-center px-4 py-12 overflow-y-auto"
                : "absolute inset-0 z-10 overflow-y-auto px-4 pt-32 pb-16"}
            style={asPopup
                ? { background: "rgba(0,0,0,0.78)", backdropFilter: "blur(10px)" }
                : {
                    background: isNight
                        ? "radial-gradient(ellipse at top, #0a0e1c 0%, #02040a 70%)"
                        : "radial-gradient(ellipse at top, #f1ece0 0%, #d8d0bd 70%)",
                }}
            onClick={asPopup ? ((e) => { if (e.target === e.currentTarget) onClose && onClose(); }) : undefined}
        >
            <div
                className={asPopup
                    ? `${isNight ? "glass" : "glass-day"} relative rounded-2xl p-6 md:p-8 w-full max-w-4xl`
                    : "max-w-5xl mx-auto fade-in"}
                style={asPopup ? { border: `1px solid ${accent}55`, boxShadow: `0 30px 80px -20px ${accent}33` } : undefined}
            >
                {asPopup && onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="bio-popup-close"
                        className="absolute top-4 right-4 w-9 h-9 rounded-full grid place-items-center"
                        style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}55` }}
                    >
                        <X size={16} />
                    </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                    {/* Portrait */}
                    <div className="md:col-span-5">
                        <div
                            className="relative aspect-[3/4] rounded-2xl overflow-hidden"
                            data-testid="bio-image-frame"
                            style={{
                                border: `1px solid ${accent}55`,
                                background: isNight ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
                                boxShadow: `0 20px 60px -20px ${accent}55`,
                            }}
                        >
                            {bio.image_url ? (
                                <img
                                    src={bio.image_url}
                                    alt="Captain"
                                    className="absolute inset-0 w-full h-full object-cover"
                                    data-testid="bio-image"
                                />
                            ) : (
                                <div
                                    className="absolute inset-0 grid place-items-center"
                                    style={{ color: accent }}
                                >
                                    <div className="text-center">
                                        <User size={48} className="mx-auto mb-3 opacity-50" />
                                        <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">
                                            No portrait yet
                                        </p>
                                    </div>
                                </div>
                            )}
                            <div
                                className="absolute bottom-0 left-0 right-0 p-4 text-center"
                                style={{
                                    background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
                                }}
                            >
                                <div
                                    className="text-[10px] tracking-[0.4em] uppercase"
                                    style={{ color: accent }}
                                >
                                    {bio.role_title || "Captain of this Vessel"}
                                </div>
                            </div>
                        </div>

                        <div className="mt-3 flex gap-2">
                            {isAdmin && (
                                <>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/webp"
                                        className="hidden"
                                        data-testid="bio-image-input"
                                        onChange={handleImage}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileRef.current?.click()}
                                        disabled={uploading}
                                        data-testid="bio-image-upload"
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                                        style={{
                                            background: `${accent}15`,
                                            color: accent,
                                            border: `1px solid ${accent}55`,
                                        }}
                                    >
                                        <UploadIcon size={12} /> {uploading ? "Uploading…" : "Photo"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditing(true)}
                                        data-testid="bio-edit"
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase"
                                        style={{ background: accent, color: "#0a0a0a" }}
                                    >
                                        <Edit3 size={12} /> Edit Bio
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Bio + links */}
                    <div className="md:col-span-7">
                        <div className="text-[10px] tracking-[0.5em] uppercase mb-3" style={{ color: accent }}>
                            HG Sea Vessel
                        </div>
                        <h1
                            className="font-serif-display text-5xl md:text-7xl uppercase tracking-luxe leading-none mb-6"
                            style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                        >
                            {bio.role_title || "Captain of this Vessel"}
                        </h1>
                        <p
                            className="whitespace-pre-wrap text-base md:text-lg font-light leading-relaxed mb-8"
                            style={{ color: isNight ? "rgba(255,255,255,0.78)" : "rgba(0,0,0,0.78)" }}
                            data-testid="bio-text"
                        >
                            {bio.text || "Tap Edit Bio to write your story."}
                        </p>

                        <div className="text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: accent }}>
                            Candle Shop · 3 Links
                        </div>
                        <div className="flex flex-col gap-3">
                            {links.map((link, i) => (
                                <a
                                    key={i}
                                    href={link.url || "#"}
                                    target={link.url ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    onClick={(e) => { if (!link.url) e.preventDefault(); }}
                                    data-testid={`candle-link-${i}`}
                                    className="inline-flex items-center justify-between gap-2 px-5 py-3 rounded-full text-xs tracking-[0.2em] uppercase transition-all hover:scale-[1.01]"
                                    style={{
                                        background: link.url ? accent : (isNight ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"),
                                        color: link.url ? "#0a0a0a" : (isNight ? "#fff" : "#0a0a0a"),
                                        border: link.url ? "none" : `1px dashed ${accent}55`,
                                        opacity: link.url ? 1 : 0.65,
                                    }}
                                >
                                    <span>{link.title || `Candle Link ${i + 1}`}</span>
                                    <ExternalLink size={14} />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit modal */}
            {editing && (
                <div
                    data-testid="bio-edit-modal"
                    className="fixed inset-0 z-50 flex items-center justify-center px-4 py-12 overflow-y-auto"
                    style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)" }}
                    onClick={(e) => { if (e.target === e.currentTarget) setEditing(false); }}
                >
                    <div
                        className="glass rounded-2xl p-6 w-full max-w-xl"
                        style={{ border: `1px solid ${accent}55` }}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: accent }}>
                                Edit Captain's Quarters
                            </div>
                            <button type="button" onClick={() => setEditing(false)} style={{ color: "rgba(255,255,255,0.7)" }} data-testid="bio-edit-close">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mb-1" style={{ color: "#fff" }}>Role Title</div>
                        <input
                            type="text"
                            value={draft.role_title}
                            onChange={(e) => setDraft({ ...draft, role_title: e.target.value })}
                            data-testid="bio-role-input"
                            className="w-full mb-4 rounded-xl px-4 py-2 outline-none text-sm"
                            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                        />
                        <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mb-1" style={{ color: "#fff" }}>Bio</div>
                        <textarea
                            value={draft.text}
                            onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                            rows={6}
                            data-testid="bio-text-input"
                            className="w-full mb-4 rounded-xl px-4 py-2 outline-none text-sm"
                            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                        />
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-2">
                                <input
                                    type="text"
                                    placeholder={`Link ${i + 1} title`}
                                    value={draft[`link${i + 1}_title`]}
                                    onChange={(e) => setDraft({ ...draft, [`link${i + 1}_title`]: e.target.value })}
                                    data-testid={`bio-link${i + 1}-title`}
                                    className="sm:col-span-2 rounded-xl px-3 py-2 text-xs outline-none"
                                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                                />
                                <input
                                    type="url"
                                    placeholder="https://"
                                    value={draft[`link${i + 1}_url`]}
                                    onChange={(e) => setDraft({ ...draft, [`link${i + 1}_url`]: e.target.value })}
                                    data-testid={`bio-link${i + 1}-url`}
                                    className="sm:col-span-3 rounded-xl px-3 py-2 text-xs outline-none"
                                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                                />
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            data-testid="bio-save"
                            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[11px] tracking-[0.3em] uppercase disabled:opacity-50"
                            style={{ background: accent, color: "#0a0a0a" }}
                        >
                            <Save size={14} /> {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
